import React from 'react'
import {
	AlertBanner,
	AlertIcon,
	AlertSub,
	AlertTitle,
	CardBody,
	IdleIntro,
	NotesArea,
	NotesGrow,
	NotesWrap,
	Section,
	SectionSubtitle,
	SectionTitle,
	SectionTitleLabel,
} from '../ItineraryImportPage.styles'
import { TripSummary } from '../types'
import { FileEntry } from '../utils'
import { AlertCircle } from './icons'
import DropZone from './DropZone'
import FileList from './FileList'
import TripContextStrip from './TripContextStrip'
import { TestIds } from '@/testIds'

interface IdleBodyProps {
	trip: TripSummary
	files: FileEntry[]
	notes: string
	isDragOver: boolean
	onNotesChange: (v: string) => void
	onBrowse: () => void
	onRemoveFile: (id: string) => void
	hasValidationErrors: boolean
	validationErrorCount: number
	serverValidationMessage?: string
}

const IdleBody: React.FC<IdleBodyProps> = ({
	trip,
	files,
	notes,
	isDragOver,
	onNotesChange,
	onBrowse,
	onRemoveFile,
	hasValidationErrors,
	validationErrorCount,
	serverValidationMessage,
}) => {
	// A refused upload names no file, so it can't say how many of them failed.
	const alertTitle =
		files.length > 0 && !serverValidationMessage
			? `${validationErrorCount} of ${files.length} files can't be imported`
			: 'Your import needs attention'

	return (
		<CardBody>
			<TripContextStrip trip={trip} />
			<IdleIntro>
				Add your bookings, handwritten notes, or anything else you have. We'll analyze your input and
				try our best to add the events to your itinerary
			</IdleIntro>
			{hasValidationErrors && (
				<AlertBanner>
					<AlertIcon>
						<AlertCircle size={16} strokeWidth={2.3} />
					</AlertIcon>
					<div>
						<AlertTitle>{alertTitle}</AlertTitle>
						<AlertSub>
							{serverValidationMessage ?? 'Fix the issues below or remove them, then continue.'}
						</AlertSub>
					</div>
				</AlertBanner>
			)}
			<Section>
				<SectionTitle>Files and photos</SectionTitle>
				<SectionSubtitle>
					PDFs, photos of your notes, or screenshots of your bookings
				</SectionSubtitle>
				{files.length === 0 ? (
					<DropZone state={isDragOver ? 'dragOver' : 'idle'} onBrowse={onBrowse} />
				) : (
					<FileList files={files} onRemove={onRemoveFile} onAddMore={onBrowse} />
				)}
			</Section>
			<Section>
				<SectionTitleLabel htmlFor="import-notes">Your notes</SectionTitleLabel>
				<SectionSubtitle>A confirmation email, a full itinerary, or your rough ideas</SectionSubtitle>
				<NotesWrap>
					<NotesGrow data-value={notes}>
						<NotesArea
							id="import-notes"
							data-testid={TestIds.aiImport.notesField}
							aria-label="Your notes"
							value={notes}
							onChange={e => onNotesChange(e.target.value)}
							placeholder="Type or paste here…"
						/>
					</NotesGrow>
				</NotesWrap>
			</Section>
		</CardBody>
	)
}

export default IdleBody
